import { put, takeEvery, call, select } from 'redux-saga/effects';
import * as actions from '../../actions';
import * as actionTypes from '../../actionTypes/ActionTypes';
import * as selectors from '../../../selectors';
import timeHelper from '../../../helpers/timeHelper';
import voucherPaymentServicesV2 from '../../../services/v2/voucherPaymentServicesV2';
import { VOUCHER_PAYMENTS_STATUS } from '../../../constants/voucher-payments.constants';

/* ---------------------------------------------
 * Helpers
 * --------------------------------------------- */

// Compare array values without lodash
const arraysEqual = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
};

// Format from/to date range if available
const buildDateRange = (filter) => {
  if (filter.from && filter.to) {
    return {
      fromDate: timeHelper.formatUtcDate(filter.from, 'YYYY-MM-DD'),
      toDate: timeHelper.formatUtcDate(filter.to, 'YYYY-MM-DD')
    };
  }
  return {};
};

// Normalize paymentSource logic
const buildPaymentSource = (src) => {
  if (src === 1) return { paymentSource: 1 };
  if (src === 'isInternalTransfer') return { isInternalTransfer: 1 };
  if (src === 'isSplitPayment') return { isSplitPayment: 1 };
  return {};
};

/* ---------------------------------------------
 * Root saga
 * --------------------------------------------- */
export default function* fetchVoucherPaymentSaga() {
  yield takeEvery(
    actionTypes.VOUCHER_PAYMENT.FETCH_VOUCHER_PAYMENT_REQUEST,
    fetchVoucherPayment
  );
}

/* ---------------------------------------------
 * Main saga
 * --------------------------------------------- */
export function* fetchVoucherPayment(action) {
  try {
    /* Step 1: Get company info */
    const company = yield select(selectors.company);
    const companyId = company?.CompanyId;
    if (!companyId) {
      yield put(actions.fetchVoucherPaymentFail('Invalid params'));
      return;
    }

    /* Step 2: Build local filter object */
    const filter = yield select(selectors.voucherPaymentFilter);

    const cloneFilter = {
      ...filter,
      companyId,
      ...(action?.payload || {})
    };

    /* Step 3: Build params for statistic API */
    const statisticParams = {
      companyId,
      keyword: cloneFilter.keyword,
      ...(action?.payload || {}),
      ...(cloneFilter.paymentType ? { paymentType: cloneFilter.paymentType } : {}),
      ...buildPaymentSource(filter.paymentSource),
      ...buildDateRange(cloneFilter)
    };

    /* Step 4: Fetch statistics */
    const statisticRes = yield call(voucherPaymentServicesV2.getStatistic, statisticParams);

    if (statisticRes?.success) {
      const stats = statisticRes.data || [];

      // Get first status that has total > 0
      const first = stats.find((item) => item.total > 0) || {};
      const detectedStatus = first.status ? String(first.status) : '0';
      let statuses = [detectedStatus];

      // Keep old statuses if they still exist in statistic data
      const filterStatuses = filter.statuses;
      if (!arraysEqual(filterStatuses, statuses)) {
        const foundMatching = stats.find(
          (item) => item.status === Number(filterStatuses) && item.total > 0
        );
        statuses = foundMatching ? filterStatuses : statuses;
      }

      cloneFilter.statuses = statuses;

      yield put(actions.getVoucherPaymentStatisticSuccess(stats));
    }

    /* Step 5: Normalize additional fields */
    cloneFilter.template = cloneFilter.template === '0' ? '' : cloneFilter.template;

    const currentStatus = Number(cloneFilter.statuses?.[0]);
    const previousStatus = filter.statuses?.[0];

    // If status changes to PAID, apply auto sort descending
    if (
      currentStatus === VOUCHER_PAYMENTS_STATUS.PAID &&
      previousStatus !== cloneFilter.statuses?.[0]
    ) {
      cloneFilter.sortType = 'desc';
    }

    /* Step 6: Build final request params */
    const requestParams = {
      ...cloneFilter,
      ...buildPaymentSource(cloneFilter.paymentSource),
      ...buildDateRange(cloneFilter),
      // Preserve old behavior: paymentSource null when not equal to 1
      ...(cloneFilter.paymentSource !== 1 ? { paymentSource: null } : {})
    };

    /* Step 7: Fetch voucher payments list */
    const listRes = yield call(voucherPaymentServicesV2.fetchAll, requestParams);

    if (listRes?.success) {
      yield put(
        actions.fetchVoucherPaymentSuccess(
          listRes.data || { total: 0, rows: [] }
        )
      );

      yield put(actions.setFilter(cloneFilter));
      return; // Prevent falling through to the fail call
    }

    yield put(actions.fetchVoucherPaymentFail(listRes?.message || 'Error'));

  } catch (err) {
    yield put(actions.fetchVoucherPaymentFail(err));
  }
}
